import {
  AssetType,
  BoxType,
  BoxTypeShape,
  ScrapBookDropMode,
  ViewMode,
  CoreEvent,
} from '@taopix/taopix-core';
import {
  AssetMetaInterface,
  ComputedPageItem,
  ObjectInterface,
} from '@taopix/taopix-core/dist/types/types';
import {
  ArrowLeftTopIcon,
  Button,
  ButtonGrid,
  CloseIcon,
  Heading,
  HelpTip,
  Horizontal,
  PopOut,
  SlidersIcon,
  SmileyStickerIcon,
  Theme,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {KeyboardKeys} from '../../Enums';
import useViewMode from '../../Hooks/useViewMode';
import {LayoutSelector, StickerCategoriesSelector, StickersSelector} from '../../Selectors';
import {filterObject} from '../../Utils/Common';
import {StickerInterface} from '../../types';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';
import StickersList from '../StickersList/StickersList';
import ToolPaletteSection, {ToolPaletteSectionProps} from '../ToolPalette/ToolPaletteSection';
import useFilter from './useFilter';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';

export type StickerPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
  sectionProps?: Partial<ToolPaletteSectionProps>;
};

const StickerPalette = React.forwardRef(
  ({sectionKey, sectionProps}: StickerPaletteProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const app = useTaopix();
    const ui = useUiState();
    const {experience} = useExperience();
    const [viewMode] = useViewMode();
    const isSmallScreen = ui.state.isSmallScreen;
    const [filter, setFilter] = useFilter<AssetMetaInterface>();
    const {t} = useTranslation(['toolpalette', 'helptips']);
    const [stickerFilterPopOutOpen, setStickerFilterPopOutOpen] = useState<boolean>(false);
    const [introTipOpen, setIntroTipOpen] = useState<boolean>(false);
    const [filterCount, setFilterCount] = useState<number>(0);
    const layout = useSelector(LayoutSelector);

    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const anchorElementRef = useRef<HTMLDivElement>(null);

    const scrapbooks = useSelector(StickersSelector).filter(asset => {
      const dropMode =
        layout.features[
          AssetType.ScrapbookClipping === asset.assetType
            ? 'scrapBookClippingDropMode'
            : 'scrapBookPictureDropMode'
        ];

      if (!ui.state.canEditPageLayout) {
        if (ScrapBookDropMode.None === dropMode.fixed) {
          return false;
        }
      } else {
        if (ScrapBookDropMode.None === dropMode.nonFixed) {
          return false;
        }
      }
      return true;
    });
    const categories = useSelector(StickerCategoriesSelector) || {};

    const hasStickers = Object.keys(scrapbooks).length >= 1;

    const statePageNumber = ui.state.pageNumber;

    const canDeviceDrag = matchMedia('(pointer: fine)').matches;

    const paletteUnavailableMessage = !ui.state.canEditPageLayout
      ? experience.EDITOR.pageEditing.allowUsersToToggleLocking
        ? t('toolpalette:str_LabelStickerPaletteUnavailable')
        : t('toolpalette:str_LabelStickerPaletteUnavailableLayoutLocked')
      : ViewMode.Grid === ui.state.viewMode
      ? t('*:str_MessagePaletteUnavailable')
      : '';

    const defaultSticker: StickerInterface = {
      assetRef: '',
      id: 0,
      thumbnailsrc: '',
      name: '',
      categoryName: '',
      assetType: AssetType.ScrapbookImage,
      asset: undefined,
    };
    const [selectedSticker, setSelectedSticker] = useState<StickerInterface>(defaultSticker);

    const onSelectSticker = (newSelectedSticker: StickerInterface) => {
      // check dropmode and either show pageoverlay/picturebox overlay or none
      let scrapBookDropMode = getScrapBookDropMode();

      // if we cannot edit the page layout don't show the overlay
      if (!ui.state.canEditPageLayout && !experience.EDITOR.editorType.useGreetingCardEditor) {
        return;
      }

      // allow scrapbooks to be dropped anywhere on a card /* TODO - Discuss this */
      if (experience.EDITOR.editorType.useGreetingCardEditor) {
        scrapBookDropMode = ScrapBookDropMode.AlwaysCreateNew;
      }

      if (ScrapBookDropMode.ExistingPictureBoxOnly === scrapBookDropMode) {
        app.core
          .dispatch('PROJECT.ADD_ASSET_SOURCE', {
            asset: newSelectedSticker.assetRef !== '' ? newSelectedSticker : null,
            aiMode: 0,
            rotation: 0,
            dynamicBehaviour: true,
            autoSelect: false,
          })
          .catch(error => {
            console.error(error);
          });
      } else {
        if (paletteOpen && newSelectedSticker.assetRef !== '') {
          if (AssetType.ScrapbookImage === newSelectedSticker.assetType) {
            app.core
              .dispatch('PAGE.ADD_NEW_ITEM', {
                pageNumber: statePageNumber,
                type: BoxType.Image,
                boxShape: BoxTypeShape.Rect,
                assetId: newSelectedSticker.assetRef,
                assetType: AssetType.ScrapbookImage,
                ignoreFixedLayout: true,
                useSelectedSide: true,
                fuzzyPosition: true,
              })
              .catch(error => {
                console.error(error);
              });
          } else if (AssetType.ScrapbookClipping === newSelectedSticker.assetType) {
            app.core
              .dispatch('PAGE.ADD_SCRAPBOOK_CLIPPING', {
                pageNumber: statePageNumber,
                ref: newSelectedSticker.assetRef,
                useSelectedSide: true,
              })
              .catch(error => {
                console.error(error);
              });
          }
        }
      }
    };

    const getScrapBookDropMode = (): number => {
      const isFixed = ui.state.canEditPageLayout;
      if (AssetType.ScrapbookImage === selectedSticker?.assetType) {
        if (!isFixed) {
          return layout.features.scrapBookPictureDropMode.fixed;
        } else {
          return layout.features.scrapBookPictureDropMode.nonFixed;
        }
      } else if (AssetType.ScrapbookClipping === selectedSticker?.assetType) {
        if (!isFixed) {
          return layout.features.scrapBookClippingDropMode.fixed;
        } else {
          return layout.features.scrapBookClippingDropMode.nonFixed;
        }
      }
      return 0;
    };

    const [gridState] = useState<ObjectInterface>({
      orientation: 'vertical',
      columns: 2, // The column count is only used if orientation is vertical; in horizontal mode there are infinite columns
      tileSize: 60, // The tileSize is only used if orientation is horizontal; in vertical mode it is determined by the column count
      gap: 10,
    });

    const hasCategories = Object.values(categories).length > 1;
    const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;

    // useEffect(() => {
    //   // check dropmode and either show pageoverlay/picutrebox overlay or none
    //   const scrapBookDropMode = getScrapBookDropMode();
    //   // if we cannot edit the page layout dont show the overlay
    //   if (!ui.state.canEditPageLayout) return;
    //   if (ScrapBookDropMode.ExistingPictureBoxOnly === scrapBookDropMode) {
    //     app.core
    //       .dispatch('PROJECT.ADD_ASSET_SOURCE', {
    //         asset: selectedSticker.assetRef !== '' ? selectedSticker : null,
    //         aiMode: 0,
    //         rotation: 0,
    //         atSource: true,
    //       })
    //       .catch(error => {
    //         console.error(error);
    //       });
    //   } else {
    //     if (paletteOpen && selectedSticker.assetRef !== '') {
    //       if (AssetType.ScrapbookImage === selectedSticker.assetType) {
    //         console.log('selectedSticker', selectedSticker);
    //         app.core
    //           .dispatch('PAGE.ADD_NEW_ITEM', {
    //             pageNumber: statePageNumber,
    //             boxType: BoxType.Image,
    //             boxShape: BoxTypeShape.Rect,
    //             assetId: selectedSticker.assetRef,
    //             assetType: AssetType.ScrapbookImage,
    //             ignoreFixedLayout: true,
    //             useSelectedSide: true,
    //           })
    //           .catch(error => {
    //             console.error(error);
    //           });
    //       } else if (AssetType.ScrapbookClipping === selectedSticker.assetType) {
    //         app.core
    //           .dispatch('PAGE.ADD_SCRAPBOOK_CLIPPING', {
    //             pageNumber: statePageNumber,
    //             ref: selectedSticker.assetRef,
    //             useSelectedSide: true,
    //           })
    //           .catch(error => {
    //             console.error(error);
    //           });
    //       }
    //     }
    //   }
    // }, [selectedSticker.assetRef]);

    useEffect(() => {
      const tokens: string[] = [];

      // If a sticker is selected and the palette is closed, remove the selection.
      if (!paletteOpen && selectedSticker.assetRef !== '') {
        setSelectedSticker(defaultSticker);
      }

      if (paletteOpen) {
        tokens.push(
          app.core.subscribe(CoreEvent.OUTSIDE_CANVAS_CLICK, (msg: string, data: any) => {
            setSelectedSticker(defaultSticker);
          })
        );
      }

      return () => {
        tokens.forEach(token => {
          app.core.unsubscribe(token);
          delete tokens[tokens.indexOf(token)];
        });
      };
    }, [paletteOpen]);

    // if we have the anchor element and the palette is open show the tip
    useEffect(() => {
      if (anchorElementRef.current && paletteOpen && canDeviceDrag) {
        setIntroTipOpen(true);
        // Close the openLayoutsPalette tip if it's open
        if (ui.state.tips.openLayoutsPalette) {
          ui.dispatch(UserInterfaceActions.setTipOpen({name: 'openLayoutsPalette', open: false}));
        }
      } else {
        setIntroTipOpen(false);
      }
    }, [anchorElementRef.current, paletteOpen]);

    const onKeyDown = (event: React.KeyboardEvent) => {
      if (KeyboardKeys.ESCAPE === event.key) {
        closeButtonRef.current?.focus();
      }
    };
    // Can we display the filter
    const canFilter = experience.EDITOR.stickers.categoryFilter;

    const stickersPopoutClasses = classNames(
      isSmallScreen ? 'sm:w-[min(600rem,theme(width.bounds))]' : 'w-[300rem]'
    );

    const filters = useMemo(() => {
      const buttonCSS = 'mt-xs ml-xs';

      if (!canFilter) {
        return <></>;
      }

      return (
        <>
          {hasCategories && (
            <Button
              ref={filterButtonRef}
              label={t('*:str_LabelFilter')}
              id={'stickersFilterButton'}
              onClick={() => {
                setStickerFilterPopOutOpen(!stickerFilterPopOutOpen);
              }}
              aria-pressed={stickerFilterPopOutOpen}
              startIcon={<SlidersIcon />}
              buttonStyle={'tertiary'}
              size={!isSmallScreen ? 'small' : 'medium'}
              badgeStyle={ThemeName.Primary}
              className={'flex-1'}
              badgeContent={(filterCount as unknown as number) > 0 ? String(filterCount) : ''}
            />
          )}
          <PopOut
            open={paletteOpen && stickerFilterPopOutOpen}
            id={'stickersFiltersPopout'}
            anchorId={isSmallScreen ? 'stickers-tool-palette' : 'stickersFilterButton'}
            onClose={() => {
              setStickerFilterPopOutOpen(false);
            }}
            excludeIdFromClickOutside={'stickersFilterButton'}
            repositionOnResize={isSmallScreen}
            displayMode={'positioned'}
            offset={isSmallScreen ? {x: 0, y: 0} : {x: 5, y: 0}}
            className={stickersPopoutClasses}
            anchorOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Right}
            }
            transformOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Left}
            }
            popDirection={isSmallScreen ? 'up' : 'right'}
            theme={'tpx-theme-toolpaletteContent'}
            themeTip={'Sidebar (Content)'}
            contentTheme={ThemeName.Popouts}
            focusFirstElement={true}
          >
            <div className={'w-full p-sm'} onKeyDown={event => onKeyDown(event)}>
              {/* close button for large screen filter popout */}
              {!isSmallScreen && (
                <div className={'absolute top-1 right-1'}>
                  <Button
                    ref={closeButtonRef}
                    label={t('toolpalette:str_LabelCloseFilterPopout')}
                    hideLabel
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setStickerFilterPopOutOpen(false);
                    }}
                    size={'small'}
                    buttonStyle={'standard'}
                  />
                </div>
              )}
              <Heading level={1} className={'text-md mb-xs'} size={4}>
                {t('str_LabelCategories', {ns: 'toolpalette'})}
              </Heading>
              <ButtonGrid>
                <>
                  <Button
                    label={t('*:str_LabelAll')}
                    buttonStyle={'tertiary'}
                    size={'small'}
                    className={buttonCSS}
                    aria-pressed={!filter?.categoryName || filter.categoryName === null}
                    onClick={() => {
                      setFilter([{categoryName: null}]);
                      setFilterCount(0);
                    }}
                  />
                  {Object.values(categories).map((item, index) => {
                    return (
                      <Button
                        key={index}
                        label={item.label}
                        buttonStyle={'tertiary'}
                        size={'small'}
                        className={buttonCSS}
                        onClick={() => {
                          setFilter([{categoryName: item.value}]);
                          setFilterCount(1);
                        }}
                        aria-pressed={filter?.categoryName && filter.categoryName === item.value}
                      />
                    );
                  })}
                </>
              </ButtonGrid>
              {/* Show a cancel button in the popout on small screen */}
              {isSmallScreen && (
                <Button
                  buttonStyle={'secondary'}
                  label={t('*:str_ButtonDone')}
                  size="medium"
                  onClick={() => {
                    setStickerFilterPopOutOpen(false);
                  }}
                  className={'w-full mt-md'}
                />
              )}
            </div>
          </PopOut>
        </>
      );
    }, [canFilter, stickerFilterPopOutOpen, categories, filter]);

    // does the experience allow us to show the palette?
    if (!experience.EDITOR.stickers.stickersPalette || !hasStickers) {
      return <></>;
    }

    const thumbnailScrollContainerClasses = classNames([
      'flex',
      'flex-col',
      'lg:flex-1',
      'px-md',
      'pb-[40rem]',
      'overflow-y-auto',
    ]);

    const topButtonContainerClasses = classNames('flex', 'pb-md', 'lg:px-md', 'lg:w-full');

    const bottomButtonsContainerClasses = classNames(
      'flex',
      'p-md',
      'lg:w-full',
      'sm:w-[min(400rem,theme(width.bounds))]',
      'sm:self-center',
      'sm:space-x-sm',
      'sm:mt-auto'
    );
    const contentWrapperClasses = classNames([
      'flex',
      'lg:flex-1',
      'flex-col',
      'min-h-0',
      'sm:h-mobilePaletteContentHeight',
    ]);

    const filteredStickers = scrapbooks.filter(s => filterObject(s, filter));

    return (
      <ToolPaletteSection
        id={'stickers-tool-palette'}
        sectionKey={sectionKey}
        useFocusMode
        title={t('str_TitleStickers', {ns: 'toolpalette'})}
        buttonIcon={<SmileyStickerIcon />}
        {...sectionProps}
        ref={ref}
      >
        {ViewMode.Grid === viewMode ||
        (!ui.state.canEditPageLayout && !experience.EDITOR.editorType.useGreetingCardEditor) ? (
          <Theme
            name={ThemeName.Container}
            className={classNames(
              'flex',
              'justify-center',
              'items-center',
              'p-md',
              'mx-md',
              'mb-md',
              'text-center',
              'italic',
              'sm:h-[245rem]'
            )}
          >
            <p>{paletteUnavailableMessage}</p>
          </Theme>
        ) : (
          <>
            <div className={contentWrapperClasses}>
              {!isSmallScreen && canFilter && hasCategories && (
                <div id="topButtonsContainer" className={topButtonContainerClasses}>
                  {filters}
                </div>
              )}
              <div
                className={thumbnailScrollContainerClasses}
                id={'stickersListContainer'}
                ref={anchorElementRef}
              >
                <StickersList
                  visible={paletteOpen}
                  stickers={filteredStickers}
                  columnGap={5}
                  columns={isSmallScreen ? 4 : 2}
                  onClick={(newSelectedSticker: StickerInterface) => {
                    onSelectSticker(newSelectedSticker);
                  }}
                  orientation={gridState.orientation}
                />
              </div>
              {isSmallScreen && canFilter && hasCategories && (
                <div id="bottomButtonsContainer" className={bottomButtonsContainerClasses}>
                  {filters}
                </div>
              )}
            </div>
            <HelpTip
              id={'stickersIntroTip'}
              open={introTipOpen && !isSmallScreen}
              message={t('helptips:str_MessageDragStickersIntoYourProject')}
              buttonLabel={t('*:str_ButtonOK')}
              buttonFunction={() => setIntroTipOpen(false)}
              anchorId={'stickersListContainer'}
              anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
              transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
              offset={{x: 10, y: 0}}
              icon={<ArrowLeftTopIcon />}
              className={'max-w-[240rem]'}
              showAfter={500}
              hideAfter={5000}
              onClose={() => setIntroTipOpen(false)}
              showMode={'once'}
            />
          </>
        )}
      </ToolPaletteSection>
    );
  }
);

export default StickerPalette;
